import { API, getAccessToken, post } from './http.ts';
import { BLOCK_SIZE } from '../lib/hash.ts';
import type { ApiNode } from './types.ts';

export interface ProbeResult {
  missing: string[];
  have: number;
  quota: { used: number; quota: number; available: number };
}

export const probeBlocks = (hashes: string[]): Promise<ProbeResult> =>
  post<ProbeResult>('/blocks/probe', { hashes });

export interface CommitInput {
  namespace_id: string;
  path: string;
  blocks: string[];
  size: number;
  content_hash: string;
  client_mtime: string;
  parent_rev: number | null;
  autorename: boolean;
}

export const commitFile = (
  input: CommitInput,
): Promise<{ node: ApiNode; journal_seq: number; created: boolean; renamed_to?: string }> =>
  post('/commit', { ...input, origin: 'web' });

export interface BlockUploadOptions {
  onProgress?: (loadedBytes: number) => void;
  signal?: AbortSignal;
}

/**
 * 블록 1개 업로드.
 * 진행률 이벤트가 필요해서 fetch 대신 XMLHttpRequest 를 쓴다
 * (fetch 는 업로드 진행률을 알려주지 않는다).
 */
export function uploadBlock(
  hash: string, blob: Blob, options: BlockUploadOptions = {},
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API}/blocks/${hash}`);
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    xhr.withCredentials = true;

    let lastLoaded = 0;
    xhr.upload.onprogress = (e) => {
      options.onProgress?.(e.loaded - lastLoaded);
      lastLoaded = e.loaded;
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { resolve(); return; }
      reject(new Error(errorMessage(xhr)));
    };
    xhr.onerror = () => { reject(new Error('네트워크 오류로 업로드하지 못했습니다')); };
    xhr.onabort = () => { reject(new DOMException('업로드가 취소되었습니다', 'AbortError')); };

    options.signal?.addEventListener('abort', () => { xhr.abort(); }, { once: true });
    xhr.send(blob);
  });
}

function errorMessage(xhr: XMLHttpRequest): string {
  try {
    const parsed = JSON.parse(xhr.responseText) as { error?: { message?: string } };
    return parsed.error?.message ?? `업로드 실패 (${xhr.status})`;
  } catch {
    return `업로드 실패 (${xhr.status})`;
  }
}

export function sliceBlocks(file: File): Blob[] {
  const parts: Blob[] = [];
  for (let offset = 0; offset < file.size; offset += BLOCK_SIZE) {
    parts.push(file.slice(offset, Math.min(offset + BLOCK_SIZE, file.size)));
  }
  return parts;
}
